'use client';

import { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import TwitterIcon from '@mui/icons-material/X'; // Import Twitter icon
import { useNavigate, useLocation } from 'react-router-dom'; // React Router for navigation

const Nav = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [visible, setVisible] = useState(true);
    const [scrollY, setScrollY] = useState(0);

    const isActive = (pathname: string): boolean => location.pathname === pathname;

    const handleDrawerToggle = (): void => {
        if (!mobileOpen) {
            setScrollY(window.scrollY); // Save current scroll position
        }
        setMobileOpen(!mobileOpen);
    };

    // Use effect to disable scrolling when the drawer is open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            window.scrollTo(0, scrollY); // Restore to saved scroll position
        }
    }, [mobileOpen, scrollY]);

    // Handle navbar visibility based on scroll
    useEffect(() => {
        const bannerHeight = 300;
        const checkScroll = () => {
            setVisible(window.scrollY < bannerHeight);
        };

        window.addEventListener('scroll', checkScroll);
        return () => window.removeEventListener('scroll', checkScroll);
    }, []);

    // Close the drawer on window resize for large screens
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > theme.breakpoints.values.sm && mobileOpen) {
                setMobileOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [mobileOpen, theme.breakpoints]);

    const handleLinkClick = (path: string): void => {
        navigate(path);
        setMobileOpen(false);
    };

    const drawer = (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                bgcolor: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
                color: '#fafafa',
                p: 2,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: theme.zIndex.drawer + 1,
            }}
        >
            <List sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                <ListItem disablePadding>
                    <Button color="inherit" onClick={() => handleLinkClick('/')} sx={{ justifyContent: 'center', width: '100%', color: isActive('/') ? '#FFD700' : 'inherit', fontSize: '20px' }}>
                        Home
                    </Button>
                </ListItem>
                <ListItem disablePadding>
                    <Button color="inherit" onClick={() => handleLinkClick('/game')} sx={{ justifyContent: 'center', width: '100%', color: isActive('/game') ? '#FFD700' : 'inherit', fontSize: '20px' }}>
                        Game
                    </Button>
                </ListItem>
                <ListItem disablePadding>
                    <Button color="inherit" onClick={() => handleLinkClick('/docs')} sx={{ justifyContent: 'center', width: '100%', color: isActive('/docs') ? '#FFD700' : 'inherit', fontSize: '20px' }}>
                        Docs
                    </Button>
                </ListItem>
                <ListItem disablePadding sx={{ justifyContent: 'center' }}>
                    <IconButton
                        color="inherit"
                        href="https://twitter.com/VrillerTCG"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: 'inherit' }}
                    >
                        <TwitterIcon fontSize="large" />
                    </IconButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <>
            <AppBar position="fixed" sx={{
                zIndex: theme.zIndex.drawer + 2,
                width: '100%',
                minHeight: { xs: 56, sm: 64 },
                background: `linear-gradient(0deg, rgba(24, 24, 27, 0.55) 0%, rgba(24, 24, 27, 1) 100%)`,
                backdropFilter: 'blur(8px)',
                transform: visible ? 'translateY(0)' : 'translateY(-100%)',
                transition: 'transform 0.2s ease-in-out',
            }}>
                <Toolbar
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        minHeight: { xs: 56, sm: 64 },
                    }}
                >
                    {/* Logo Section */}
                    <Box onClick={() => handleLinkClick('/')} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <Typography
                            variant="h6"
                            sx={{ fontFamily: "'Blacc', 'sans-serif'", fontSize: 34, flexShrink: 0, color: 'inherit' }}
                        >
                            Vriller
                        </Typography>
                        <Box
                            component="svg"
                            viewBox="0 0 100 100"
                            sx={{ width: 24, height: 24, ml: 1, color: 'inherit', position: 'relative', top: '0px' }}
                        >
                            <path
                                d="M50 4 L50 90 M50 40 L20 10 M50 40 L80 10"
                                stroke="currentColor"
                                strokeWidth="12"
                                fill="none"
                            />
                        </Box>
                    </Box>

                    {/* Centered Buttons Section */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: { xs: 'none', sm: 'flex' },
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Button color="inherit" onClick={() => handleLinkClick('/')} sx={{
                            fontWeight: "bold",
                            fontFamily: "'Cinzel', serif",
                            color: isActive('/') ? '#d4af37' : 'inherit',
                        }}>
                            Home
                        </Button>
                        <Button color="inherit" onClick={() => handleLinkClick('/game')} sx={{
                            fontWeight: "bold",
                            fontFamily: "'Cinzel', serif",
                            color: isActive('/game') ? '#FFD700' : 'inherit',
                        }}>
                            Game
                        </Button>
                        <Button color="inherit" onClick={() => handleLinkClick('/docs')} sx={{
                            fontWeight: "bold",
                            fontFamily: "'Cinzel', serif",
                            color: isActive('/docs') ? '#FFD700' : 'inherit',
                        }}>
                            Docs
                        </Button>
                        {/* Twitter Icon */}
                        <IconButton
                            color="inherit"
                            href="https://twitter.com/VrillerTCG"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'inherit' }}
                        >
                            <TwitterIcon />
                        </IconButton>
                    </Box>

                    {/* Account and Menu Icon */}
                    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        <IconButton color="inherit"><AccountCircleIcon /></IconButton>
                        <IconButton color="inherit" edge="end" onClick={handleDrawerToggle} sx={{ display: { sm: 'none' } }}>
                            <MenuIcon />
                        </IconButton>
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer
                anchor="top"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true
                }}
                BackdropProps={{
                    sx: {
                        bgcolor: 'transparent',
                    }
                }}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '100%',
                        height: '100%',
                        bgcolor: 'transparent',
                        overflow: 'hidden',
                    }
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
};

export default Nav;